/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";

import { QuestionnaireItemType } from "@medplum/react";

import { DateInput, DateValue } from "@mantine/dates";

import { INTAKE_FORM_INITIAL_VALUES } from "@/intake-forms/constants/intake-forms";
import { useStableCalendarDateInputState } from "@/intake-forms/hooks/useStableCalendarDateInputState";
import { IntakeFormComponentProps } from "@/intake-forms/types/intake-forms";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { parseMantineDateInputValue } from "@/utils/mantine-date-input";

export default function QuestionnaireDateField<T>({
  form,
  formFieldName,
  label,
  required,
  name,
  placeholder,
  autoPopulate,
  leftSection,
  clearable = true,
  canSelectFutureDates = true,
}: Omit<IntakeFormComponentProps<T>, "key"> & {
  error?: string;
  leftSection?: React.ReactNode;
  clearable?: boolean;
}) {
  const formInputProps = form.getInputProps(formFieldName);
  const [fieldPath, setFieldPath] = useState<string>("");

  const { stableDateValue, calendarDate, handleCalendarDateChange } =
    useStableCalendarDateInputState(formInputProps.value);

  useEffect(() => {
    if (autoPopulate && !formInputProps.value && formInputProps.onChange) {
      const utcDate = INTAKE_FORM_INITIAL_VALUES[QuestionnaireItemType.date] as Date | null;
      if (utcDate) {
        formInputProps.onChange(
          new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate()),
        );
      } else {
        formInputProps.onChange(new Date());
      }
    }
    const isInHorizontalGroup = formFieldName.includes(".");
    const computedFieldPath = isInHorizontalGroup ? formFieldName : name;
    setFieldPath(computedFieldPath);
  }, [formInputProps, autoPopulate, formFieldName, name]);

  const handleChange = (val: DateValue) => {
    if (!val) {
      form.setFieldValue(fieldPath, undefined as any);
      return;
    }
    form.setFieldValue(fieldPath, parseMantineDateInputValue(val) as any);
  };

  return (
    <DateInput
      key={name}
      name={name}
      label={label}
      date={calendarDate}
      clearable={clearable}
      withAsterisk={required}
      onChange={handleChange}
      value={stableDateValue}
      valueFormat="MM/DD/YYYY"
      leftSection={leftSection}
      placeholder={placeholder}
      error={form.errors[formFieldName]}
      onDateChange={handleCalendarDateChange}
      allowDeselect={clearable ? false : undefined}
      data-testid={questionnaireFieldTestId(formFieldName)}
      maxDate={canSelectFutureDates ? undefined : new Date()}
    />
  );
}
